import type { IdleAgent, ProjectMember } from '../transport/api'
import type { ConvBusyAgent } from '../servers/convBusy'
import { ownerDisplayName } from './rosterGroups'
import { presenceFor } from '../agents/useIdleStatus'
import { PixelPet } from '../identity/PixelPet'

/**
 * #projAgentBusy — the floating top-right busy-agents indicator.
 *
 * Port of projRenderBusyAgents (index.html ~5986), since widened (owner,
 * 2026-08-26: 讓 Owner 的朋友也能夠看到 Agent 的忙碌狀態): it shows EVERY agent
 * attached to THIS project that is working, not only the viewer's own — each as a
 * headshot ringed by a spinning green halo. Vanishes entirely when none are busy.
 * The container ignores pointer events (purely informational, never eats chat
 * clicks); only a tappable chip takes them.
 *
 * TWO SOURCES, ONE TRUTH. The viewer's own agents read from `byHash` — their
 * browser's direct ask of their own machines (freshest). Everyone else's agents
 * read from `convBusy` — the BFF's conversation-scoped relay of the SAME
 * /agent/idle_status question to the hosting machines. A not-mine agent never
 * reads from byHash: my machines cannot host someone else's agent, and a stray
 * hash match must not light another person's halo.
 *
 * THE SUB-LABEL IS THE OWNER, NOT THE MACHINE (owner, 2026-08-26: 下標 Owner 的
 * 名稱，而非機器的名稱). A friend reading "@gmicloud-loki-g4" learns nothing —
 * whose machine is that? The person's name answers the question the room
 * actually has: whose agent is working. Resolved from the members roster
 * (owner_user → that human's profile name), owner_email as the fallback, and
 * only when no owner resolves at all does the old @server text return.
 *
 * Tapping jumps straight to that agent's conversation — only for the viewer's
 * OWN agents (the idle payload carries session/host; a friend cannot open
 * someone else's terminal, so their chips stay informational).
 *
 * Stable keys (channel_hash) keep React from replacing the DOM nodes on each
 * refetch, which is what stops the halo animation restarting.
 */
export function BusyAgents({
  members,
  myEmail,
  byHash,
  byKey,
  convBusy,
  onJump,
}: {
  members: ProjectMember[]
  myEmail: string | null
  byHash: Map<string, IdleAgent>
  byKey: Map<string, IdleAgent>
  convBusy: Map<string, ConvBusyAgent>
  onJump: (session: string, host: string | null) => void
}) {
  const me = (myEmail ?? '').toLowerCase()
  // "My own agent" — the SAME test the members roster uses (ProjectPanel), and for
  // the same reason: the vaultless router names an agent's owner by device principal
  // (`device:<fp>`), which no Google session email ever equals, so
  // `owner_email === myEmail` was false for EVERY agent and this float never appeared
  // (owner, 2026-08-14). `m.mine` is the edge's stamp — it performed the attach —
  // and is the reliable signal here; the email check stays as the fallback.
  const isMine = (m: ProjectMember) =>
    m.mine === true || (!!me && String(m.owner_email ?? '').toLowerCase() === me)

  const rows = members
    .filter((m) => m.kind === 'agent' && !!m.channel_hash)
    .map((m) => {
      const hash = m.channel_hash!
      const mine = isMine(m)
      const idle = mine ? byHash.get(hash) : undefined
      /*
       * MY OWN agent's busy verdict is the edge's PUSHED switch, read through
       * presenceFor — the same source the rail dots use. This USED to read the
       * fetched row's `.status`, which only moves when the idle-status QUERY re-runs
       * (window focus, a user action, a page refresh). The edge's live `idle_status`
       * push updates the switch store, NOT the fetched row, so a working agent's halo
       * appeared only after a refresh (owner, 2026-09-03). A friend's agent has no
       * switch on this machine — the fan-out diet sends none — so it stays on
       * convBusy, which useConvBusy refetches on the room's chat_busy poke.
       */
      const active = mine
        ? !!idle && presenceFor(byKey, idle) === 'working'
        : convBusy.get(hash)?.status === 'active'
      return { m, hash, mine, idle, active }
    })
    .filter((r) => r.active)
  if (rows.length === 0) return null

  return (
    <div id="projAgentBusy" aria-live="polite" aria-label="Busy agents">
      {rows.map(({ m, hash, idle }) => {
        // The label may already be "name @ server": split so the server never
        // rides into the name half.
        const raw = m.label ?? `agent ${hash.slice(0, 6)}`
        const parts = raw.split(/\s+@\s+/)
        const name = (parts[0] ?? raw).trim()
        const srv = (idle?.host || parts.slice(1).join(' @ ') || '').trim()
        const ownerName = ownerDisplayName(members, m)
        const sub = ownerName ?? (srv ? `@${srv}` : '(local)')
        const sess = idle?.session ?? ''

        return (
          <div
            key={hash}
            className={`pab-chip${sess ? ' tappable' : ''}`}
            title={
              `${name}${ownerName ? ` — ${ownerName}'s agent` : ''}` +
              `${srv ? ` @${srv}` : ''} — working…` +
              (sess ? ' · tap to open this agent' : '')
            }
            {...(sess
              ? {
                  role: 'button',
                  tabIndex: 0,
                  onClick: () => onJump(sess, srv || null),
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onJump(sess, srv || null)
                    }
                  },
                }
              : {})}
          >
            <span className="pab-av">
              <span className="pab-avatar">
                <PixelPet name={name} seed={hash} size={30} active />
              </span>
            </span>
            <span className="pab-meta">
              <span className="pab-name">{name}</span>
              <span className="pab-srv">{sub}</span>
            </span>
          </div>
        )
      })}
    </div>
  )
}
